import CustomButton from "components/CustomButton";
import { InputSelect } from "components/CustomInputs/InputSelect";
import { Separator } from "components/ui/separator";
import { ChevronsLeft, Loader2, Send, Square, SquareCheckBig } from "lucide-react";
import ups from "../../../../assets/UPS.svg";
import fedex from "../../../../assets/FedEx.svg";
import { cn } from "lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CaixaEnvio, Estagio1Data } from "./Estagio1";
import type { Caixa } from "services/caixas";
import { Estagio2Data } from "../ModalRealizarCotacao";
import { postCompareShipment } from "services/realizarCotacao/realizarCotacao.service";
import { convertPedidoToShipmentPedido } from "utils/convertPedidoToShipmentPedido";
import type {
  CaixaShipment,
  CarrierQuote,
  Quotes,
} from "services/realizarCotacao/realizarCotacao.types";
import { cotarUPS, emitirUPS } from "services/ups";
import { cotarFEDEX, emitirFEDEX } from "services/fedex";
import type { UpsRateRequest, UpsShipRequest } from "types/upsTypes";
import type {
  FedexRequest,
  FedexEndereco,
  FedexShipResponseRaw,
} from "types/fedexTypes";
import { anexarDocsCotacao, confirmarFreteShipment } from "services/api";
import {
  getCobrancaCotacao,
  pagarCotacaoComSaldo,
} from "services/cotacoes/cotacoes.service";
import type { CobrancaCotacao } from "services/cotacoes/cotacoes.types";
import { criarBoletoCotacao } from "services/boleto";
import type { BoletoCotacaoDetalhe } from "services/boleto/boleto.types";
import { useAuth } from "hooks/useAuth";
import { getCarteira } from "services/carteira/carteira.service";

const SHIPPER_NUMBER = process.env.REACT_APP_UPS_ACCOUNT_NUMBER || "K9E789";

// Feature beta: cobrança antecipada de frete + desembaraço + impostos.
// Só aparece para a conta de teste.
const IMPOSTOS_BETA_EMAIL = "leonardo@exportadigital.com";
const ymdPlusDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const cleanZip = (country?: string, v?: string) => {
  const raw = String(v ?? "")
    .trim()
    .toUpperCase();
  if (country === "BR") return raw.replace(/\D/g, "");
  return raw.replace(/\s+/g, "");
};
const toNum = (v: unknown) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const moneyStr = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};
const qtyStr = (v: unknown) => {
  const x = parseInt(String(v ?? "").replace(/\D+/g, ""), 10);
  if (!x || x < 1) return "1";
  return String(Math.min(x, 9_999_999));
};
const addrArr = (rua?: string) => [String(rua || "").trim()].filter(Boolean);
const mimeDaEtiquetaUps = (tipo?: string) => {
  const t = String(tipo || "").toUpperCase();
  if (t === "PNG") return "image/png";
  if (t === "GIF") return "image/gif";
  if (t === "ZPL") return "text/plain";
  if (t === "PDF") return "application/pdf";
  return undefined;
};
export const iso2Country = (v?: string) => {
  const x = String(v ?? "")
    .trim()
    .toUpperCase();
  const map: Record<string, string> = {
    BR: "BR",
    BRA: "BR",
    BRASIL: "BR",
    BRAZIL: "BR",
    US: "US",
    USA: "US",
    "UNITED STATES": "US",
    UNITEDSTATES: "US",
    CA: "CA",
    CANADA: "CA",
    MX: "MX",
    MEXICO: "MX",
    AR: "AR",
    ARGENTINA: "AR",
    CL: "CL",
    CHILE: "CL",
    GB: "GB",
    UK: "GB",
    "UNITED KINGDOM": "GB",
  };
  return map[x] || (x.length === 2 ? x : "");
};

// Uma etiqueta de volume a caminho do backend: base64 (UPS) ou URL (FedEx).
type EntradaEtiqueta = { base64?: string; url?: string; mime?: string };

type PacoteEnvio = {
  weightKg: number;
  dimCm: { height: number; width: number; length: number };
};

type CaixasResolvidas = {
  caixa: CaixaShipment[];
  packages: PacoteEnvio[];
  packagesId: string[];
  pesoTotal: number;
  // Só vem preenchido quando cada volume tem peso próprio (cotação manual
  // multi-caixa); aí o backend não redistribui o peso total.
  pesosCaixasKg: number[] | null;
};

/**
 * Peso dos produtos, sem embalagem.
 *
 * O que o pedido importado trouxe por item (`peso_total_kg`) vale como padrão,
 * mas nem toda loja manda peso por produto — e quando não manda, o campo do
 * Estágio 1 é a única fonte. Esse campo existia e era validado, só que nunca
 * chegava até aqui: a pessoa digitava um peso que não entrava na cotação.
 */
const pesoProdutosKg = (informado: string, doPedido: number) =>
  toNum(informado) > 0 ? toNum(informado) : doPedido;

/**
 * Normaliza os dois jeitos de informar volume: a lista multi-caixa do fluxo
 * manual e a caixa única do fluxo com CSV. Daqui para baixo o resto do estágio
 * trabalha sempre com N volumes.
 */
const resolverCaixas = (
  caixas: CaixaEnvio[] | undefined,
  selectedCaixa: Caixa | undefined,
  pesoPedidoKg: number,
): CaixasResolvidas => {
  const lista: CaixaEnvio[] = caixas?.length
    ? caixas
    : selectedCaixa
      ? [
          {
            entryId: "",
            caixaId: String(selectedCaixa.id),
            caixa: selectedCaixa,
            pesoKg: 0,
            itens: [],
          },
        ]
      : [];

  const caixa: CaixaShipment[] = lista
    .filter((c): c is CaixaEnvio & { caixa: NonNullable<CaixaEnvio["caixa"]> } =>
      Boolean(c.caixa),
    )
    .map((c) => ({
      ...c.caixa,
      entryId: c.entryId,
      ...(c.pesoKg > 0 ? { pesoKg: String(c.pesoKg) } : {}),
      ...(c.itens?.length ? { itens: c.itens } : {}),
    }));

  const pesosCaixasKg =
    caixa.length && caixa.every((c) => toNum(c.pesoKg) > 0)
      ? caixa.map((c) => toNum(c.pesoKg))
      : null;

  // Tara: o `peso` do cadastro e a caixa VAZIA, nunca o envio montado.
  const pesoCaixasVazias = caixa.reduce((acc, c) => acc + toNum(c.peso), 0);

  // O que a transportadora pesa e produto + embalagem. Cada ramo daqui pegava
  // so uma das pontas: o fluxo com CSV mandava o peso dos produtos sem a caixa,
  // ou o peso da caixa sem os produtos, e a cotacao saia abaixo do peso real —
  // a diferenca aparecia depois, na fatura da transportadora.
  //
  // `pesosCaixasKg` ja vem somado (caixa vazia + itens de cada volume) do
  // fluxo manual, entao nao leva a tara de novo.
  const pesoTotal = pesosCaixasKg
    ? pesosCaixasKg.reduce((acc, p) => acc + p, 0)
    : pesoPedidoKg > 0
      ? pesoPedidoKg + pesoCaixasVazias
      : pesoCaixasVazias > 0
        ? pesoCaixasVazias
        : 0.5;

  const pesoPorCaixa = Math.max(pesoTotal / (caixa.length || 1), 0.001);

  const packages: PacoteEnvio[] = caixa.map((c, i) => ({
    weightKg: pesosCaixasKg ? Math.max(pesosCaixasKg[i], 0.001) : pesoPorCaixa,
    dimCm: {
      height: toNum(c.altura) || 1,
      width: toNum(c.largura) || 1,
      length: toNum(c.comprimento) || 1,
    },
  }));

  const packagesId = caixa
    .map((c) => String(c.id))
    .filter((id) => id && id !== "undefined" && id !== "null");

  return { caixa, packages, packagesId, pesoTotal, pesosCaixasKg };
};

/**
 * No fluxo manual o destino e digitado no Estagio 2 e o `pedido` e montado no
 * Estagio 1, entao ele chegava ao backend com pais vazio. Resultado: a cotacao
 * nascia com pais_dest null e a estimativa de imposto abortava com
 * 'sem_pais_destino' — nenhum envio manual era estimado.
 */
const completarDestino = <T extends Record<string, any>>(
  pedido: T,
  destinatario: Estagio2Data["destinatario"],
): T => ({
  ...pedido,
  pais: iso2Country(destinatario.country) || pedido.pais || "",
  cidade: destinatario.city || pedido.cidade || "",
  estado: destinatario.state || pedido.estado || "",
  CEP: destinatario.cep || pedido.CEP || "",
  endereco: destinatario.rua || pedido.endereco || "",
  nomeComprador: destinatario.nome || pedido.nomeComprador || "",
});

const toFedexEndereco = (p: Estagio2Data["remetente"]): FedexEndereco => ({
  nome: p.nome,
  telefone: p.telefone,
  rua: p.rua,
  cidade: p.city,
  estado: p.state,
  cep: p.cep,
  pais: iso2Country(p.country),
});

type Props = {
  previous: () => void;
  estagio1: Estagio1Data;
  estagio2: Estagio2Data;
  estagio: number;
  setShow: (value: React.SetStateAction<boolean>) => void;
  /** Troca para o modal da Carteira Intrex (indice 14 na sidebar). */
  abrirCarteira?: () => void;
};

// Enquanto a finalidade do envio esta em teste, o campo so aparece para esta
// conta. As outras seguem emitindo sem declarar nada, como sempre foi.
const CONTAS_COM_FINALIDADE = ["leonardo@exportadigital.com"];

// A invoice tem que ser em inglês, entao o texto ja vai em inglês.
const DECLARACAO_LABORATORIO =
  "Samples shipped for laboratory testing and analysis purposes only. Not for resale.";

// `purpose` e o valor do enum que a FedEx aceita em shipmentPurpose — ele imprime
// em "Purpose of Shipment" com o texto da propria FedEx ("Sample"), nao com o
// rotulo daqui. A frase completa quem carrega e a declaracao, que sai no bloco
// "Declaration Statement(s)".
const FINALIDADES = [
  { value: "PADRAO", label: "Padrão (não declarar)", purpose: null, declaracao: "" },
  {
    value: "SAMPLE_LAB",
    label: "Samples for laboratory testing",
    purpose: "SAMPLE",
    declaracao: DECLARACAO_LABORATORIO,
  },
  { value: "SAMPLE", label: "Amostra (geral)", purpose: "SAMPLE", declaracao: "" },
  { value: "SOLD", label: "Venda", purpose: "SOLD", declaracao: "" },
  { value: "NOT_SOLD", label: "Não vendido", purpose: "NOT_SOLD", declaracao: "" },
  { value: "GIFT", label: "Brinde", purpose: "GIFT", declaracao: "" },
  {
    value: "REPAIR_AND_RETURN",
    label: "Reparo e devolução",
    purpose: "REPAIR_AND_RETURN",
    declaracao: "",
  },
];

const OPCOES_FINALIDADE = FINALIDADES.map(({ value, label }) => ({ value, label }));

const Estagio3 = ({
  previous,
  estagio1,
  estagio2,
  estagio,
  setShow,
  abrirCarteira,
}: Props) => {
  const { t } = useTranslation("dashboard");
  const [selectedOption, setOption] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finalidade, setFinalidade] = useState("");
  const [declaracao, setDeclaracao] = useState("");
  const purposeFedex =
    FINALIDADES.find((f) => f.value === finalidade)?.purpose ?? null;
  const [quotes, setQuotes] = useState<Quotes>({});
  const [shipmentId, setShipmentId] = useState<number | null>(null);

  // Fluxo novo: confirmar a transportadora cria a cotacao e PARA. O cliente ve
  // o total, paga, e so entao a documentacao e emitida. Antes um botao so
  // fazia as duas coisas e nao havia onde cobrar.
  const [cotacaoConfirmada, setCotacaoConfirmada] = useState<number | null>(null);
  const [cobranca, setCobranca] = useState<CobrancaCotacao | null>(null);
  const [erroCobranca, setErroCobranca] = useState<string | null>(null);
  const [verDetalhes, setVerDetalhes] = useState(false);
  // Pagar antes da estimativa chegar cobraria a remessa sem o imposto. O
  // servidor também recusa; aqui só evita o clique que daria erro.
  const aguardandoImposto =
    !!cobranca &&
    cobranca.imposto_habilitado !== false &&
    !cobranca.imposto_estimado &&
    !cobranca.pagamento?.pago;
  const [pagando, setPagando] = useState(false);
  const [emitindo, setEmitindo] = useState(false);

  // ===== Feature beta: boleto com impostos =====
  // Liga por e-mail da conta de teste OU por ?betaimpostos=1 na URL
  // (o parâmetro fica salvo no navegador). Não afeta os demais clientes.
  const { user } = useAuth();

  // Campo de finalidade do envio, ainda em teste: mesma conta do beta acima,
  // mas em constante separada porque sao features diferentes e podem ser
  // liberadas para contas diferentes.
  const mostraFinalidade = CONTAS_COM_FINALIDADE.includes(
    String(user?.email || "").trim().toLowerCase(),
  );
  const [betaImpostos, setBetaImpostos] = useState<boolean>(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.has("betaimpostos")) {
        window.localStorage.setItem("betaImpostos", "1");
        return true;
      }
      if (window.localStorage.getItem("betaImpostos") === "1") return true;
    } catch {
      /* ignore */
    }
    return false;
  });
  useEffect(() => {
    if ((user?.email || "").toLowerCase() === IMPOSTOS_BETA_EMAIL) {
      setBetaImpostos(true);
    }
  }, [user?.email]);
  const [cotacaoIdEmitida, setCotacaoIdEmitida] = useState<number | null>(null);
  const [vencimentoBoleto, setVencimentoBoleto] = useState<string>(
    ymdPlusDays(3),
  );
  const [gerandoBoleto, setGerandoBoleto] = useState(false);
  const [boletoDetalhe, setBoletoDetalhe] =
    useState<BoletoCotacaoDetalhe | null>(null);
  const [boletoUrl, setBoletoUrl] = useState<string | null>(null);

  const gerarBoletoComImpostos = async () => {
    if (!cotacaoIdEmitida) return;
    setError(null);
    setGerandoBoleto(true);
    try {
      const res = await criarBoletoCotacao(cotacaoIdEmitida, vencimentoBoleto);
      setBoletoDetalhe(res.detalhe);
      setBoletoUrl(res.boleto?.bankSlipUrl || null);
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        null;
      setError(apiMsg || "Falha ao gerar o boleto com impostos.");
    } finally {
      setGerandoBoleto(false);
    }
  };

  useEffect(() => {
    if (estagio !== 2) return;
    const selectedPedido = estagio1.selectedPedido;
    const temCaixa = Boolean(
      estagio1.caixas?.length || estagio1.selectedCaixa,
    );
    if (!selectedPedido || !temCaixa) return;
    const pedidoComHscode = completarDestino(
      {
        ...selectedPedido,
        // A moeda escolhida no passo 1 manda. Sem isto o backend usava a
        // moeda da importacao (pedido.moeda): pedido vindo da Shopify em
        // BRL era despachado declarando real, mesmo com dolar selecionado
        // na tela. A FedEx nao emite invoice comercial em BRL saindo do
        // Brasil, entao o envio saia com etiqueta e sem invoice.
        moeda: estagio1.currencyPrice || selectedPedido.moeda,
        itens: (selectedPedido.itens || []).map((it, idx) => ({
          ...it,
          hscode: estagio1.hscodes?.[idx] || it.hscode || "",
          moedaLinha: estagio1.currencyPrice || it.moedaLinha,
        })),
      },
      estagio2.destinatario,
    );
    const pedidoManual =
      pedidoComHscode && Array.isArray(pedidoComHscode.itens)
        ? pedidoComHscode
        : null;

    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const pedido = convertPedidoToShipmentPedido(selectedPedido);

        const remetente = estagio2.remetente;
        const destinatario = estagio2.destinatario;

        const hasPessoa = (p: Estagio2Data["remetente"]) =>
          p.nome && p.rua && p.city && p.cep && p.country;

        if (!hasPessoa(remetente) || !hasPessoa(destinatario)) {
          throw new Error("Cadastro de remetente/destinatário incompleto.");
        }

        const { caixa, packages, packagesId, pesoTotal, pesosCaixasKg } =
          resolverCaixas(
            estagio1.caixas,
            estagio1.selectedCaixa,
            pesoProdutosKg(estagio1.pesoTotal, pedido.peso_total_kg),
          );

        if (!packages.length) {
          throw new Error("Nenhuma caixa informada para o envio.");
        }

        const upsPackages: UpsRateRequest["RateRequest"]["Shipment"]["Package"] =
          packages.map((p) => ({
            PackagingType: { Code: "02" },
            Dimensions: {
              UnitOfMeasurement: { Code: "CM" },
              Length: String(p.dimCm.length || 0),
              Width: String(p.dimCm.width || 0),
              Height: String(p.dimCm.height || 0),
            },
            PackageWeight: {
              UnitOfMeasurement: { Code: "KGS" },
              Weight: String(Math.max(p.weightKg, 0.001)),
            },
          }));

        const shipperAddressLine = remetente.rua || "";
        const shipToAddressLine = destinatario.rua || "";
        const pickupYMD = new Date()
          .toISOString()
          .slice(0, 10)
          .replace(/-/g, "");
        const remetenteZip = cleanZip(remetente.country, remetente.cep);
        console.log("REMETENTE:", {
          country: remetente.country,
          cep: remetente.cep,
          zip: remetenteZip,
        });
        if (!remetenteZip) {
          throw new Error("CEP/ZIP do remetente é obrigatório e inválido.");
        }

        const upsRatePayload: UpsRateRequest = {
          RateRequest: {
            Request: {
              RequestOption: "Rate",
              TransactionReference: {
                CustomerContext: "compare",
                TransactionIdentifier: "String",
              },
            },
            PickupType: { Code: "01" },
            CustomerClassification: { Code: "01" },
            Shipment: {
              Shipper: {
                ShipperNumber: SHIPPER_NUMBER,
                Address: {
                  AddressLine: [shipperAddressLine],
                  City: remetente.city || "",
                  StateProvinceCode: (remetente.state || "").toUpperCase(),
                  PostalCode: remetenteZip,
                  CountryCode: iso2Country(remetente.country),
                },
              },
              ShipFrom: {
                Address: {
                  AddressLine: [shipperAddressLine],
                  City: remetente.city || "",
                  StateProvinceCode: (remetente.state || "").toUpperCase(),
                  PostalCode: remetenteZip,
                  CountryCode: iso2Country(remetente.country),
                },
              },
              ShipTo: {
                Address: {
                  AddressLine: [shipToAddressLine],
                  City: destinatario.city || "",
                  StateProvinceCode: (destinatario.state || "").toUpperCase(),
                  PostalCode: cleanZip(destinatario.country, destinatario.cep),
                  CountryCode: iso2Country(destinatario.country),
                },
              },
              Service: { Code: "08" },
              ShipmentTotalWeight: {
                UnitOfMeasurement: { Code: "KGS" },
                Weight: String(pesoTotal),
              },
              Package: upsPackages.length
                ? upsPackages
                : [
                    {
                      PackagingType: { Code: "02" },
                      Dimensions: {
                        UnitOfMeasurement: { Code: "CM" },
                        Length: "10",
                        Width: "8",
                        Height: "6",
                      },
                      PackageWeight: {
                        UnitOfMeasurement: { Code: "KGS" },
                        Weight: "2",
                      },
                    },
                  ],
              ShipmentRatingOptions: { NegotiatedRatesIndicator: "Y" },
              InvoiceLineTotal: {
                CurrencyCode: estagio1.currencyPrice || pedido.moeda || "USD",
                MonetaryValue: String(Number(pedido.total || 1) || 1),
              },
              DeliveryTimeInformation: {
                PackageBillType: "03",
                Pickup: { Date: pickupYMD || "20250101" },
              },
            },
          },
        };

        const fedexRatePayload: FedexRequest = {
          pedido_ref: String(pedido.pedido_ref),
          packagesId,
          pesoTotalPedidoKg: pesoTotal,
          ...(pesosCaixasKg ? { pesosCaixasKg } : {}),
          shipper: toFedexEndereco(remetente),
          recipient: toFedexEndereco(destinatario),
          ...(pedidoManual ? { pedido_manual: pedidoManual } : {}),
        };

        let upsRateRes: any = null;
        let fedexRateRes: any = null;

        try {
          upsRateRes = await cotarUPS(upsRatePayload);
        } catch {
          // noop
        }

        try {
          fedexRateRes = await cotarFEDEX(fedexRatePayload);
        } catch {
          // noop
        }

        if (!upsRateRes && !fedexRateRes) {
          throw new Error("Nenhuma cotação foi gerada.");
        }

        const rows: Array<{
          carrier: "UPS" | "FEDEX";
          base: number;
          total: number;
          carrier_raw: any;
        }> = [];

        if (upsRateRes) {
          const rated = upsRateRes?.raw?.RateResponse?.RatedShipment;
          const list = Array.isArray(rated) ? rated : rated ? [rated] : [];
          const chosen = list[0];

          const base =
            Number(
              chosen?.NegotiatedRateCharges?.BaseServiceCharge?.MonetaryValue,
            ) || 0;
          const negotiated =
            Number(chosen?.NegotiatedRateCharges?.TotalCharge?.MonetaryValue) ||
            Number(chosen?.TotalCharges?.MonetaryValue) ||
            Number(chosen?.TransportationCharges?.MonetaryValue) ||
            0;

          rows.push({
            carrier: "UPS",
            base,
            total: negotiated,
            carrier_raw: upsRateRes?.raw || upsRateRes,
          });
        }

        if (fedexRateRes) {
          const svcs = Array.isArray(fedexRateRes?.services)
            ? fedexRateRes.services
            : [];
          const chosen = svcs[0];
          if (chosen) {
            rows.push({
              carrier: "FEDEX",
              base: Number(chosen.base || 0),
              total: Number(chosen.total || chosen.base || 0),
              carrier_raw: fedexRateRes?.raw || fedexRateRes,
            });
          }
        }

        const respCompare = await postCompareShipment({
          pedido_ref: pedido.pedido_ref,
          pedido,
          caixa,
          pesoTotalPedidoKg: pesoTotal,
          pedido_manual: pedidoManual,
          ups_rate_payload:
            rows.find((r) => r.carrier === "UPS")?.carrier_raw || null,
          fedex_rate_payload:
            rows.find((r) => r.carrier === "FEDEX")?.carrier_raw || null,
          preco_base_ups: rows.find((r) => r.carrier === "UPS")?.base,
          preco_base_fedex: rows.find((r) => r.carrier === "FEDEX")?.base,
        });

        if (!respCompare?.ok) {
          const respAny: any = respCompare;
          const errMsg = respAny?.error || respAny?.errors || null;
          throw new Error(errMsg || "Falha ao comparar fretes.");
        }

        setQuotes(respCompare.quotes || {});
        setShipmentId(Number(respCompare.shipment_id) || null);
      } catch (err: any) {
        if (!alive) return;
        const apiMsg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          null;

        // 403 com envios_bloqueados e decisao comercial, nao falha tecnica:
        // mostrar "falha ao comparar fretes" faria o cliente tentar de novo
        // para sempre e abrir chamado de bug.
        if (err?.response?.data?.envios_bloqueados) {
          setError(
            apiMsg ||
              "Os envios desta conta estão bloqueados. Fale com o suporte da Intrex.",
          );
          return;
        }

        setError(
          apiMsg ||
            t("modal_make_quotes.error_compare", "Falha ao comparar fretes."),
        );
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    estagio,
    estagio1.selectedPedido,
    estagio1.selectedCaixa,
    estagio1.caixas,
    estagio1.hscodes,
    // Entra na conta do peso quando o pedido nao traz peso por item: sem isso
    // a comparacao ficaria com o valor anterior ao que a pessoa digitou.
    estagio1.pesoTotal,
    estagio2.remetente,
    estagio2.destinatario,
    t,
  ]);

  const upsQuote: CarrierQuote | undefined = quotes.UPS;
  const fedexQuote: CarrierQuote | undefined = quotes.FEDEX;

  // Preço mostrado ao cliente: o backend devolve total_cliente = (frete +
  // taxas) já com o markup do plano. base/total ficam com o valor puro da
  // transportadora, que é o que segue nos payloads de emissão.
  const upsCost = useMemo(() => {
    const total = Number(
      upsQuote?.total_cliente ?? upsQuote?.total ?? upsQuote?.base ?? 0,
    );
    return Number.isFinite(total) ? total : 0;
  }, [upsQuote]);

  const fedexCost = useMemo(() => {
    const total = Number(
      fedexQuote?.total_cliente ?? fedexQuote?.total ?? fedexQuote?.base ?? 0,
    );
    return Number.isFinite(total) ? total : 0;
  }, [fedexQuote]);

  const handleConfirm = async () => {
    setError(null);
    if (!shipmentId) {
      setError("Cotação não encontrada. Refaça a comparação.");
      return;
    }
    if (selectedOption !== "UPS" && selectedOption !== "FEDEX") {
      setError("Selecione uma transportadora.");
      return;
    }
    const quote = selectedOption === "UPS" ? quotes.UPS : quotes.FEDEX;
    if (!quote) {
      setError("Transportadora sem cotação disponível.");
      return;
    }
    try {
      setConfirming(true);
      const resp = await confirmarFreteShipment(shipmentId, selectedOption);
      const respAny: any = resp?.data || resp;
      const cotacaoId = Number(respAny?.cotacao_id) || null;
      if (!cotacaoId) {
        throw new Error("Cotação não retornou id.");
      }
      const cobrancaAntecipada = await getCarteira()
        .then((r) => r.habilitada === true)
        .catch(() => false);

      /*
       * Emite sempre, inclusive para quem tem cobranca antecipada.
       *
       * Antes a emissao so acontecia depois do pagamento. Quem fechava a
       * janela antes de pagar ficava com uma cotacao orfa: sem rastreio, sem
       * etiqueta e sem invoice — e sem como retomar, porque o payload da
       * transportadora so existe na memoria deste estagio.
       *
       * A trava de pagamento passa a valer onde ela faz efeito: no DOWNLOAD
       * do documento. O envio nasce na transportadora, o rastreio aparece, e
       * a etiqueta so sai da tela depois de pago.
       *
       * Contrapartida assumida: emitir nao tira dinheiro na hora — a
       * transportadora so cobra depois, na fatura. Mas o envio criado entra
       * nessa fatura, entao a Intrex paga por ele mesmo se o cliente nunca
       * pagar a ela. Toda conta fora da allowlist ja funcionava assim; isso
       * alinha as duas.
       */
      await emitirDocumentacao(cotacaoId, { manterAberto: cobrancaAntecipada });

      // Com cobranca antecipada, o painel de pagamento entra na sequencia.
      if (cobrancaAntecipada) setCotacaoConfirmada(cotacaoId);
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        null;
      setError(apiMsg || "Falha ao confirmar o frete.");
    } finally {
      setConfirming(false);
    }
  };

  const emitirDocumentacao = async (
    cotacaoId: number,
    opcoes?: { manterAberto?: boolean },
  ) => {
    setError(null);
    const quote = selectedOption === "UPS" ? quotes.UPS : quotes.FEDEX;
    if (!quote) {
      setError("Transportadora sem cotação disponível.");
      return;
    }

    try {
      setEmitindo(true);
      const selectedPedido = estagio1.selectedPedido;
      if (!selectedPedido) {
        throw new Error("Pedido não encontrado.");
      }
      const pedidoComHscode = completarDestino(
        {
          ...selectedPedido,
          // A moeda escolhida no passo 1 manda. Sem isto o backend usava a
          // moeda da importacao (pedido.moeda): pedido vindo da Shopify em
          // BRL era despachado declarando real, mesmo com dolar selecionado
          // na tela. A FedEx nao emite invoice comercial em BRL saindo do
          // Brasil, entao o envio saia com etiqueta e sem invoice.
          moeda: estagio1.currencyPrice || selectedPedido.moeda,
          itens: (selectedPedido.itens || []).map((it, idx) => ({
            ...it,
            hscode: estagio1.hscodes?.[idx] || it.hscode || "",
            moedaLinha: estagio1.currencyPrice || it.moedaLinha,
          })),
        },
        estagio2.destinatario,
      );
      const pedidoManual =
        pedidoComHscode && Array.isArray(pedidoComHscode.itens)
          ? pedidoComHscode
          : null;

      const remetente = estagio2.remetente;
      const destinatario = estagio2.destinatario;

      const items = selectedPedido.itens.map((it, idx) => ({
        descricao: it.descricao || it.titulo || it.sku || "Item",
        qty: Number(it.qty || 1),
        unitPrice: Number(it.preco || 0),
        hscode: estagio1.hscodes?.[idx] || it.hscode || "",
        countryOfOrigin: "BR",
        weightKg: toNum(it.pesoUnit) || 0.1,
      }));

      const declaredTotal = items
        .reduce((acc, it) => acc + it.unitPrice * it.qty, 0)
        .toFixed(2);

      const pedidoShip = convertPedidoToShipmentPedido(selectedPedido);
      const { packages, packagesId, pesoTotal, pesosCaixasKg } = resolverCaixas(
        estagio1.caixas,
        estagio1.selectedCaixa,
        pesoProdutosKg(estagio1.pesoTotal, pedidoShip.peso_total_kg),
      );

      if (!packages.length) {
        throw new Error("Nenhuma caixa informada para o envio.");
      }

      const remetenteZip = cleanZip(remetente.country, remetente.cep);
      console.log("REMETENTE:", {
        country: remetente.country,
        cep: remetente.cep,
        zip: cleanZip(remetente.country, remetente.cep),
      });

      if (selectedOption === "UPS") {
        const serviceCode = quote?.serviceCode || "08";
        const shipPayload: UpsShipRequest = {
          ShipmentRequest: {
            Request: {
              RequestOption: "nonvalidate",
              TransactionReference: {
                CustomerContext: "",
                TransactionIdentifier: "String",
              },
            },
            Shipment: {
              Description: "Description of Goods",
              Shipper: {
                Name: remetente.nome || "",
                AttentionName: remetente.nome || "",
                ShipperNumber: SHIPPER_NUMBER,
                Phone: { Number: remetente.telefone || "" },
                Address: {
                  AddressLine: addrArr(remetente.rua),
                  City: remetente.city || "",
                  StateProvinceCode: (remetente.state || "").toUpperCase(),
                  PostalCode: remetenteZip,
                  CountryCode: iso2Country(remetente.country),
                },
              },
              ShipTo: {
                Name: destinatario.nome || "",
                AttentionName: destinatario.nome || "",
                Phone: { Number: destinatario.telefone || "" },
                Address: {
                  AddressLine: addrArr(destinatario.rua),
                  City: destinatario.city || "",
                  StateProvinceCode: (destinatario.state || "").toUpperCase(),
                  PostalCode: cleanZip(destinatario.country, destinatario.cep),
                  CountryCode: iso2Country(destinatario.country),
                },
              },
              ShipFrom: {
                Name: remetente.nome || "",
                AttentionName: remetente.nome || "",
                Phone: { Number: remetente.telefone || "" },
                Address: {
                  AddressLine: addrArr(remetente.rua),
                  City: remetente.city || "",
                  StateProvinceCode: (remetente.state || "").toUpperCase(),
                  PostalCode: remetenteZip,
                  CountryCode: iso2Country(remetente.country),
                },
              },
              PaymentInformation: {
                ShipmentCharge: {
                  Type: "01",
                  BillShipper: { AccountNumber: SHIPPER_NUMBER },
                },
              },
              Service: { Code: String(serviceCode), Description: "Expedited" },
              Package: packages.map((p) => ({
                Description: "International Goods",
                Packaging: { Code: "02" },
                PackageWeight: {
                  UnitOfMeasurement: { Code: "KGS" },
                  Weight: String(p.weightKg || 0),
                },
                Dimensions: {
                  UnitOfMeasurement: { Code: "CM" },
                  Length: String(p.dimCm.length),
                  Width: String(p.dimCm.width),
                  Height: String(p.dimCm.height),
                },
              })),
              ShipmentServiceOptions: {
                InternationalForms: {
                  FormType: "01",
                  ImageFormat: { Code: "PDF" },
                  InvoiceNumber: `INV-${selectedPedido.pedido_ref || Date.now()}`,
                  InvoiceDate: new Date()
                    .toISOString()
                    .slice(0, 10)
                    .replace(/-/g, ""),
                  CurrencyCode: estagio1.currencyPrice || selectedPedido.moeda || "USD",
                  TermsOfShipment: estagio2.triangulacao,
                  ReasonForExport: "SALE",
                  FreightCharges: {
                    CurrencyCode: quote?.currency || "USD",
                    MonetaryValue: moneyStr(quote?.total || quote?.base || 0),
                  },
                  InvoiceLineTotal: {
                    CurrencyCode: estagio1.currencyPrice || selectedPedido.moeda || "USD",
                    MonetaryValue: declaredTotal,
                  },
                  Contacts: {
                    SoldFrom: {
                      Option: "01",
                      Name: remetente.nome || "",
                      AttentionName: remetente.nome || "",
                      Phone: { Number: remetente.telefone || "" },
                      Address: {
                        AddressLine: addrArr(remetente.rua),
                        City: remetente.city || "",
                        StateProvinceCode: (
                          remetente.state || ""
                        ).toUpperCase(),
                        PostalCode: cleanZip(remetente.country, remetente.cep),
                        CountryCode: iso2Country(remetente.country),
                      },
                    },
                    SoldTo: {
                      Option: "01",
                      Name: destinatario.nome || "",
                      AttentionName: destinatario.nome || "",
                      Phone: { Number: destinatario.telefone || "" },
                      Address: {
                        AddressLine: addrArr(destinatario.rua),
                        City: destinatario.city || "",
                        StateProvinceCode: (
                          destinatario.state || ""
                        ).toUpperCase(),
                        PostalCode: cleanZip(
                          destinatario.country,
                          destinatario.cep,
                        ),
                        CountryCode: iso2Country(destinatario.country),
                      },
                    },
                    Producer: {
                      Option: "01",
                      CompanyName: remetente.nome || "",
                      Address: {
                        AddressLine: addrArr(remetente.rua),
                        City: remetente.city || "",
                        StateProvinceCode: (
                          remetente.state || ""
                        ).toUpperCase(),
                        PostalCode: cleanZip(remetente.country, remetente.cep),
                        CountryCode: iso2Country(remetente.country),
                      },
                      Phone: { Number: remetente.telefone || "" },
                    },
                  },
                  Product: items.map((p, i) => ({
                    Description: p.descricao || `Item ${i + 1}`,
                    CommodityCode: p.hscode || "00000000",
                    OriginCountryCode: p.countryOfOrigin || "BR",
                    Unit: {
                      UnitOfMeasurement: { Code: "PCS" },
                      Number: qtyStr(p.qty),
                      Value: moneyStr(p.unitPrice),
                    },
                    UnitPrice: moneyStr(p.unitPrice),
                    NetCostCode: "NC",
                    PreferenceCriteria: "A",
                  })),
                },
              },
            },
            RateInformation: { NegotiatedRatesIndicator: "Y" },
            LabelSpecification: { LabelImageFormat: { Code: "PDF" } },
            LabelDelivery: { LabelLinksIndicator: "" },
          },
        };

        const shipRaw = await emitirUPS({ ...shipPayload, cotacaoId });
        const lbl = shipRaw.label;
        const invoiceB64: string | undefined =
          shipRaw?.invoice?.b64 || undefined;
        const invoiceMime: string | undefined =
          shipRaw?.invoice?.mime ||
          (invoiceB64 ? "application/pdf" : undefined);

        const tn = shipRaw?.trackingNumbers?.[0] || undefined;
        const isB64 = lbl && typeof (lbl as any).b64 === "string";
        const label_base64 = isB64 ? ((lbl as any).b64 as string) : undefined;
        const label_type = lbl?.type as
          | "PNG"
          | "GIF"
          | "ZPL"
          | "PDF"
          | undefined;
        const label_mime = mimeDaEtiquetaUps(label_type);

        // Multi-volume: a UPS devolve um PackageResults (e uma etiqueta) por
        // caixa. Manda todas; o backend junta num PDF de N páginas.
        const etiquetasUps: EntradaEtiqueta[] = (shipRaw?.labels ?? []).flatMap(
          (l): EntradaEtiqueta[] =>
            l?.b64
              ? [{ base64: l.b64, mime: mimeDaEtiquetaUps(l.type) }]
              : l?.href
                ? [{ url: l.href }]
                : [],
        );

        await anexarDocsCotacao(cotacaoId, {
          ...(etiquetasUps.length
            ? { etiquetas: etiquetasUps }
            : { etiqueta_base64: label_base64, etiqueta_mime: label_mime }),
          tracking_number: tn,
          invoice_base64: invoiceB64,
          invoice_mime: invoiceMime || "application/pdf",
        });
      }

      if (selectedOption === "FEDEX") {
        const shipPayload: FedexRequest = {
          pedido_ref: String(selectedPedido.pedido_ref),
          packagesId,
          pesoTotalPedidoKg: pesoTotal,
          ...(pesosCaixasKg ? { pesosCaixasKg } : {}),
          freight_total: Number(quote?.total || 0) || undefined,
          rate_payload: quote?.carrier_raw || undefined,
          shipper: toFedexEndereco(remetente),
          recipient: toFedexEndereco(destinatario),
          cotacaoId,
          ...(pedidoManual ? { pedido_manual: pedidoManual } : {}),
          ...(mostraFinalidade && purposeFedex
            ? { shipmentPurpose: purposeFedex }
            : {}),
          ...(mostraFinalidade && declaracao.trim()
            ? { declarationStatement: declaracao.trim() }
            : {}),
        };

        const shipRaw: FedexShipResponseRaw = await emitirFEDEX(shipPayload);
        const ts = shipRaw?.raw?.output?.transactionShipments?.[0];
        const pieceResponses = Array.isArray(ts?.pieceResponses)
          ? ts!.pieceResponses
          : [];
        const tns = [
          ts?.masterTrackingNumber,
          ...pieceResponses.map((p: any) => p?.trackingNumber),
        ].filter(Boolean) as string[];

        const shipmentDocs = Array.isArray(ts?.shipmentDocuments)
          ? ts!.shipmentDocuments
          : [];
        /*
         * So vale o documento que e de fato a invoice comercial.
         *
         * Havia um `|| shipmentDocs[0]` de fallback aqui: quando a FedEx nao
         * devolvia a invoice, o primeiro documento da lista era a etiqueta
         * mesclada, e ela ia para o campo da invoice. O cliente baixava
         * "invoice" e recebia a etiqueta.
         */
        const invoiceDoc = shipmentDocs.find((d: any) =>
          /INVOICE/i.test(String(d?.contentType || d?.docType || "")),
        );
        // Uma etiqueta por volume: cada pieceResponse do MPS traz a sua.
        const etiquetasFedex: EntradaEtiqueta[] = pieceResponses.flatMap(
          (p: any): EntradaEtiqueta[] => {
            const docs = Array.isArray(p?.packageDocuments)
              ? p.packageDocuments
              : [];
            const doc =
              docs.find((d: any) => d?.contentType === "LABEL") || docs[0];
            return doc?.url ? [{ url: String(doc.url) }] : [];
          },
        );

        await anexarDocsCotacao(cotacaoId, {
          tracking_number: tns?.[0],
          ...(etiquetasFedex.length
            ? { etiquetas: etiquetasFedex }
            : { etiqueta_url: undefined }),
          ...(invoiceDoc?.url ? { invoice_url: invoiceDoc.url } : {}),
        } as any);

        // A remessa ja foi criada e a etiqueta saiu: isto nao e erro, e um
        // aviso de que falta o documento. Sem ele a pessoa so descobria
        // quando o cliente reclamava que o botao da invoice estava cinza.
        const avisos: string[] = Array.isArray((shipRaw as any)?.avisos)
          ? (shipRaw as any).avisos
          : [];
        if (!invoiceDoc?.url || avisos.length) {
          toast.warning(
            "Envio criado, mas a invoice não veio da FedEx. Baixe no portal da FedEx e anexe em Cotações.",
            { duration: 12000 },
          );
        }
      }

      if (betaImpostos && selectedOption === "FEDEX") {
        // mantém o modal aberto para o passo de simulação do boleto com impostos
        setCotacaoIdEmitida(cotacaoId);
      } else if (!opcoes?.manterAberto) {
        // Com cobrança antecipada o modal segue aberto: o pagamento é o
        // próximo passo, e fechar aqui devolveria a cotação órfã de antes.
        setShow(false);
      }
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        null;
      setError(apiMsg || "Falha ao emitir a documentação.");
    } finally {
      setEmitindo(false);
    }
  };

  // Enquanto a cotacao existe e nao foi paga, a tela fica consultando: a
  // estimativa de imposto demora ~35s (EUA) a ~2min (Europa) e o pagamento por
  // boleto compensa fora daqui. Para quando ja estiver paga.
  useEffect(() => {
    if (!cotacaoConfirmada) return;

    let vivo = true;
    let timer: ReturnType<typeof setTimeout>;

    // Tentativa que falha nao pode ficar girando em silencio: "calculando" e
    // "a rota nao respondeu" ficavam identicos na tela.
    let falhas = 0;

    const consultar = async () => {
      try {
        const c = await getCobrancaCotacao(cotacaoConfirmada);
        if (!vivo) return;
        falhas = 0;
        setErroCobranca(null);
        setCobranca(c);

        // So vale insistir se houver algo a esperar: estimativa que ainda vai
        // chegar, ou boleto que ainda vai compensar. Conta fora da allowlist
        // nunca estima — ficar consultando so gastava requisicao e deixava a
        // tela dizendo "calculando" para sempre.
        // `!== false` e nao `===  true`: backend mais antigo nao devolve estes
        // campos, e com `&&` a ausencia virava "nao espere nada" — a tela
        // parava de consultar na primeira resposta e ficava em "calculando"
        // para sempre, mesmo com a estimativa chegando segundos depois.
        const esperandoImposto =
          c.imposto_habilitado !== false && !c.imposto_estimado;
        const esperandoPagamento =
          c.cobranca_habilitada !== false && !c.pagamento?.pago;

        if (esperandoImposto || esperandoPagamento) {
          timer = setTimeout(consultar, esperandoImposto ? 8000 : 15000);
        }
      } catch (err: any) {
        if (!vivo) return;
        falhas += 1;

        const status = err?.response?.status;
        const msg =
          status === 404
            ? "O servidor ainda não tem a rota de cobrança (/cotacoes/:id/cobranca). Falta publicar o backend."
            : err?.response?.data?.error ||
              err?.message ||
              "Não foi possível calcular o total.";

        // Duas tentativas antes de desistir: pode ser instabilidade de rede.
        if (falhas >= 2) {
          setErroCobranca(msg);
          return;
        }
        timer = setTimeout(consultar, 5000);
      }
    };

    consultar();
    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, [cotacaoConfirmada]);

  const pagarComCarteira = async () => {
    if (!cotacaoConfirmada) return;
    setError(null);
    setPagando(true);
    try {
      await pagarCotacaoComSaldo(cotacaoConfirmada);
      const c = await getCobrancaCotacao(cotacaoConfirmada);
      setCobranca(c);
    } catch (err: any) {
      const apiMsg = err?.response?.data?.error || err?.message;
      setError(apiMsg || "Não foi possível pagar com o saldo.");
    } finally {
      setPagando(false);
    }
  };

  return (
    <>
      {betaImpostos ? (
        <p className="mb-2 rounded bg-emerald-100 px-2 py-1 text-center text-xs text-emerald-800">
          beta impostos ATIVO — o boleto com impostos aparece após emitir a
          FedEx
        </p>
      ) : null}
      <div className="min-h-52 max-w-2xl m-auto flex justify-evenly p-4">
        <OptionCard
          logo={fedex}
          alt="logo da Fedex"
          custo={fedexCost}
          desciption={t("modal_make_quotes.delivery_time")}
          action={() => setOption("FEDEX")}
          selected={selectedOption === "FEDEX"}
          loading={loading}
          unavailable={!loading && !fedexQuote}
        />
        <OptionCard
          logo={ups}
          alt="logo da UPS"
          custo={upsCost}
          desciption={t("modal_make_quotes.delivery_time")}
          action={() => setOption("UPS")}
          selected={selectedOption === "UPS"}
          loading={loading}
          unavailable={!loading && !upsQuote}
        />
      </div>
      {error ? <p className="text-sm text-red-500 mb-2">{error}</p> : null}
      {mostraFinalidade ? (
        <div className="mb-4 w-full rounded-xl border border-brand-text/20 bg-brand-bg p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-brand-text/60">
            Finalidade do envio (invoice)
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <InputSelect
              label="Finalidade"
              options={OPCOES_FINALIDADE}
              value={finalidade}
              onValueChange={(v) => {
                setFinalidade(v);
                // A opcao ja traz o texto pronto; so preenche se o campo estiver
                // vazio, para nao apagar o que o operador escreveu.
                const escolha = FINALIDADES.find((f) => f.value === v);
                if (escolha?.declaracao && !declaracao.trim()) {
                  setDeclaracao(escolha.declaracao);
                }
              }}
            />
            <div className="flex flex-col gap-1">
              <label className="text-sm text-brand-text" htmlFor="declaracao-invoice">
                Declaração na invoice (em inglês)
              </label>
              <textarea
                id="declaracao-invoice"
                value={declaracao}
                onChange={(e) => setDeclaracao(e.target.value.slice(0, 450))}
                rows={3}
                placeholder={DECLARACAO_LABORATORIO}
                className="w-full rounded-lg border border-brand-text/20 bg-white p-2 text-sm text-brand-text outline-none focus:border-brand-primary"
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-brand-text/50">
            Sai em &quot;Purpose of Shipment&quot; e &quot;Declaration Statement(s)&quot; na
            commercial invoice. Em branco, a invoice sai como hoje. Só FedEx por enquanto.
          </p>
        </div>
      ) : null}
      <Separator className="my-4 w-full bg-brand-text pt-0.5" />
      <div className="grid grid-cols-2 w-full gap-4">
        <CustomButton
          label={t("buttons.prev_step")}
          icon={<ChevronsLeft />}
          iconDirection="left"
          onClick={previous}
        />
        <CustomButton
          label={confirming ? "Gerando documentação..." : t("buttons.send")}
          icon={confirming ? <Loader2 className="animate-spin" /> : <Send />}
          onClick={handleConfirm}
          disabled={
            loading || confirming || !selectedOption || !!cotacaoConfirmada
          }
          className={`
            transition
            ${(loading || confirming || !selectedOption) && "opacity-50 cursor-not-allowed pointer-events-none"}
          `}
        />
      </div>

      {cotacaoConfirmada ? (
        <div className="mt-6 w-full rounded-xl border-2 border-brand-text/20 bg-brand-bg p-4 text-brand-text">
          <p className="mb-1 text-sm font-semibold">
            Cotacao #{cotacaoConfirmada} criada &mdash;{" "}
            {cobranca?.pagamento?.pago
              ? "pagamento confirmado"
              : "pague para liberar a documentacao"}
          </p>

          {!cobranca && erroCobranca ? (
            <div className="py-3">
              <p className="text-sm text-red-500">{erroCobranca}</p>
              <p className="mt-1 text-xs text-brand-text/60">
                A cotacao #{cotacaoConfirmada} foi criada e esta salva. Da para
                pagar e emitir por Cotacoes depois que isso for resolvido.
              </p>
            </div>
          ) : null}

          {!cobranca && !erroCobranca ? (
            <p className="flex items-center gap-2 py-4 text-sm text-brand-text/60">
              <Loader2 className="h-4 w-4 animate-spin" /> calculando o total...
            </p>
          ) : null}

          {cobranca ? (
            <>
              <table className="mt-3 w-full border-collapse text-sm">
                <tbody>
                  <tr className="border-b border-brand-text/15">
                    <td className="py-1 pr-2 text-brand-text/70">
                      Frete (ja com a taxa de servico)
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      R$ {cobranca.frete_brl.toFixed(2)}
                    </td>
                  </tr>
                  <tr className="border-b border-brand-text/15">
                    <td className="py-1 pr-2 text-brand-text/70">
                      Desembaraco da transportadora
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      R$ {cobranca.desembaraco_brl.toFixed(2)}
                    </td>
                  </tr>
                  <tr className="border-b border-brand-text/15">
                    <td className="py-1 pr-2 text-brand-text/70">
                      Impostos do destino
                      {!cobranca.imposto_cobravel && cobranca.imposto_estimado
                        ? " (estimado, nao cobrado)"
                        : ""}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {cobranca.imposto_estimado ? (
                        <span>
                          R${" "}
                          {(cobranca.imposto_cobravel
                            ? cobranca.imposto_brl
                            : cobranca.imposto_estimado_brl ??
                              cobranca.imposto_brl
                          ).toFixed(2)}
                        </span>
                      ) : cobranca.imposto_habilitado !== false ? (
                        <span className="inline-flex items-center gap-1 text-xs text-brand-text/50">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          calculando (ate ~3 min)
                        </span>
                      ) : (
                        <span className="text-xs text-brand-text/50">
                          nao calculado nesta conta
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-2 text-base font-semibold">
                      Total a pagar
                    </td>
                    <td className="py-2 text-right text-base font-semibold tabular-nums">
                      R$ {cobranca.total_brl.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {cobranca.imposto_estimado && !cobranca.imposto_cobravel ? (
                <p className="mt-2 text-xs text-amber-600">
                  O imposto acima e estimativa e NAO esta somado no total: esta
                  conta roda em modo de conferencia. Ele sera cobrado na entrega
                  pela transportadora, como sempre foi.
                </p>
              ) : null}

              {cobranca.imposto_habilitado === false ? (
                <p className="mt-2 text-xs text-amber-600">
                  Esta conta esta fora do beta de impostos, entao o total cobre
                  frete e desembaraco. O imposto do destino sera cobrado na
                  entrega, como sempre foi.
                </p>
              ) : null}

              <button
                type="button"
                className="mt-1 text-xs font-semibold text-brand-primary underline"
                onClick={() => setVerDetalhes((v) => !v)}
              >
                {verDetalhes
                  ? "Ocultar detalhes"
                  : "Ver detalhes do frete e dos impostos"}
              </button>

              {verDetalhes ? (
                <div className="mt-2 rounded-lg bg-brand-card p-3 text-xs text-brand-text/70">
                  <p className="tabular-nums">
                    Frete USD {cobranca.frete_usd.toFixed(2)} + desembaraco USD{" "}
                    {cobranca.desembaraco_usd.toFixed(2)} &mdash; convertidos a
                    R$ {cobranca.fx.toFixed(4)} por dolar.
                  </p>
                  {cobranca.breakdown.length ? (
                    <ul className="mt-2 list-disc pl-4">
                      {cobranca.breakdown.map((b, i) => (
                        <li key={i}>
                          {b.nome || "Tributo"}
                          {b.aliquota_pct ? " (" + b.aliquota_pct + "%)" : ""}
                          {": "}
                          {b.moeda} {Number(b.valor).toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2">
                      Detalhamento do imposto ainda nao disponivel.
                    </p>
                  )}
                </div>
              ) : null}

              {cobranca.pagamento?.pago ? (
                <div className="mt-4">
                  <p className="mb-2 text-sm text-emerald-600">
                    {cobranca.cobranca_habilitada
                      ? `Pago via ${
                          cobranca.pagamento.forma === "carteira"
                            ? "carteira"
                            : "boleto"
                        }. Pode emitir a documentacao.`
                      : "Pode emitir a documentacao."}
                  </p>
                  <CustomButton
                    label={emitindo ? "Emitindo..." : "Emitir documentacao"}
                    icon={
                      emitindo ? <Loader2 className="animate-spin" /> : <Send />
                    }
                    onClick={() => emitirDocumentacao(cotacaoConfirmada)}
                    disabled={emitindo}
                    className={emitindo ? "opacity-50 pointer-events-none" : ""}
                  />
                </div>
              ) : (
                <>
                  <p className="mt-3 text-xs text-brand-text/60">
                    Saldo da carteira: R${" "}
                    {(cobranca.saldo_centavos / 100).toFixed(2)}
                    {!cobranca.saldo_suficiente
                      ? " \u2014 insuficiente para esta remessa"
                      : ""}
                  </p>

                  {cobranca.pagamento?.boleto_pendente ? (
                    <p className="mt-1 text-xs text-amber-600">
                      Boleto gerado, aguardando compensacao. A documentacao
                      libera sozinha quando o Asaas confirmar.
                    </p>
                  ) : null}

                  <div className="mt-3 flex flex-wrap gap-3">
                    <CustomButton
                      label={pagando ? "Pagando..." : "Pagar com a carteira"}
                      icon={
                        pagando ? <Loader2 className="animate-spin" /> : <Send />
                      }
                      onClick={pagarComCarteira}
                      disabled={pagando || !cobranca.saldo_suficiente || aguardandoImposto}
                      className={
                        pagando || !cobranca.saldo_suficiente || aguardandoImposto
                          ? "opacity-50 pointer-events-none"
                          : ""
                      }
                    />
                    {abrirCarteira ? (
                      <CustomButton
                        label="Adicionar saldo"
                        className="bg-transparent border-brand-text text-brand-text hover:bg-brand-text hover:text-white"
                        onClick={abrirCarteira}
                      />
                    ) : null}
                    <CustomButton
                      label={gerandoBoleto ? "Gerando..." : "Gerar boleto"}
                      className={`bg-transparent border-brand-text text-brand-text hover:bg-brand-text hover:text-white ${aguardandoImposto ? "opacity-50 pointer-events-none" : ""}`}
                      disabled={gerandoBoleto || aguardandoImposto}
                      onClick={async () => {
                        setGerandoBoleto(true);
                        setError(null);
                        try {
                          const r = await criarBoletoCotacao(
                            cotacaoConfirmada,
                            vencimentoBoleto,
                          );
                          setBoletoUrl(r.boleto?.bankSlipUrl || null);
                          setCobranca(
                            await getCobrancaCotacao(cotacaoConfirmada),
                          );
                        } catch (err: any) {
                          setError(
                            err?.response?.data?.error ||
                              "Nao foi possivel gerar o boleto.",
                          );
                        } finally {
                          setGerandoBoleto(false);
                        }
                      }}
                    />
                    {boletoUrl ? (
                      <CustomButton
                        label="Abrir boleto"
                        className="bg-transparent border-brand-text text-brand-text hover:bg-brand-text hover:text-white"
                        onClick={() => window.open(boletoUrl, "_blank")}
                      />
                    ) : null}
                  </div>
                </>
              )}
            </>
          ) : null}
        </div>
      ) : null}

      {betaImpostos && cotacaoIdEmitida && selectedOption === "FEDEX" ? (
        <div className="mt-6 w-full rounded-xl border border-brand-text/40 bg-brand-card p-4 text-brand-text">
          <p className="mb-1 text-sm font-semibold">
            Boleto com impostos (beta) — cotação #{cotacaoIdEmitida}
          </p>
          <p className="mb-3 text-xs text-brand-text/60">
            Frete + desembaraço + imposto de importação estimado + margem +
            câmbio. Não bloqueia o envio nesta etapa.
          </p>

          <div className="mb-3 flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs">
              Vencimento
              <input
                type="date"
                value={vencimentoBoleto}
                min={ymdPlusDays(1)}
                onChange={(e) => setVencimentoBoleto(e.target.value)}
                className="mt-1 rounded border border-brand-text/40 bg-transparent px-2 py-1 text-sm"
              />
            </label>
            <CustomButton
              label={gerandoBoleto ? "Gerando..." : "Gerar boleto com impostos"}
              icon={
                gerandoBoleto ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Send />
                )
              }
              onClick={gerarBoletoComImpostos}
              disabled={gerandoBoleto || !vencimentoBoleto}
            />
          </div>

          {boletoDetalhe ? (
            <div className="text-sm">
              {/* Dois blocos separados de propósito: frete é serviço da Intrex,
                  imposto e desembaraço são repasse. Misturar as linhas fazia o
                  cliente ler margem de câmbio como se fosse preço do frete. */}
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-brand-text/60">
                Frete
              </p>
              <table className="w-full border-collapse">
                <tbody>
                  <tr className="border-b border-brand-text/15">
                    <td className="py-1 pr-2 text-brand-text/70">
                      Frete (já com a taxa de serviço)
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      R$ {boletoDetalhe.frete_brl?.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="mb-1 mt-4 text-xs font-bold uppercase tracking-wide text-brand-text/60">
                Impostos e taxas
              </p>
              <table className="w-full border-collapse">
                <tbody>
                  {(Array.isArray(boletoDetalhe.breakdown)
                    ? boletoDetalhe.breakdown
                    : []
                  ).map((b, i) => (
                    <tr key={i} className="border-b border-brand-text/15">
                      <td className="py-1 pr-2 text-brand-text/70">
                        {/* `nome` é o formato atual; `tipo`/`descricao` são os
                            snapshots antigos, da época do EDT. */}
                        {b.nome || b.descricao || b.tipo || "Tributo"}
                        {b.aliquota_pct ? ` (${b.aliquota_pct}%)` : ""}
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        {b.moeda} {Number(b.valor).toFixed(2)}
                      </td>
                    </tr>
                  ))}

                  {boletoDetalhe.desembaraco_usd ? (
                    <tr className="border-b border-brand-text/15">
                      <td className="py-1 pr-2 text-brand-text/70">
                        Desembaraço da transportadora
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        USD {boletoDetalhe.desembaraco_usd?.toFixed(2)}
                      </td>
                    </tr>
                  ) : null}

                  <tr className="border-b border-brand-text/15">
                    <td className="py-1 pr-2 font-medium">
                      Impostos e taxas em real
                    </td>
                    <td className="py-1 text-right font-medium tabular-nums">
                      R${" "}
                      {(
                        (boletoDetalhe.imposto_brl || 0) +
                        (boletoDetalhe.desembaraco_brl || 0)
                      ).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className="mt-3 w-full border-collapse">
                <tbody>
                  <tr>
                    <td className="py-1 pr-2 text-base font-semibold">
                      Total a pagar
                    </td>
                    <td className="py-1 text-right text-base font-semibold tabular-nums">
                      R$ {boletoDetalhe.total_brl?.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="mt-2 text-xs text-brand-text/60">
                Imposto <strong>estimado</strong> sobre o valor da mercadoria —
                frete não entra na base. Convertido a R${" "}
                {boletoDetalhe.fx?.toFixed(4)} por dólar
                {boletoDetalhe.margem_pct != null
                  ? `, com margem de ${boletoDetalhe.margem_pct}%`
                  : ""}
                {boletoDetalhe.colchao_cambio_pct != null
                  ? ` e colchão de câmbio de ${boletoDetalhe.colchao_cambio_pct}%`
                  : ""}
                . O valor cobrado pela aduana do destino pode variar.
              </p>

              {boletoDetalhe.de_minimis ? (
                <p className="mt-2 text-xs text-amber-600">
                  A estimativa concluiu que esta remessa é isenta de imposto no
                  destino (de minimis). Confira antes de despachar.
                </p>
              ) : null}

              <div className="mt-3 flex gap-3">
                {boletoUrl ? (
                  <CustomButton
                    label="Abrir boleto"
                    onClick={() => window.open(boletoUrl, "_blank")}
                  />
                ) : null}
                <CustomButton label="Fechar" onClick={() => setShow(false)} />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
};
export default Estagio3;

type CardProp = {
  logo: string;
  alt: string;
  custo: number;
  desciption: string;
  action: () => void;
  selected: boolean;
  loading?: boolean;
  unavailable?: boolean;
};

export const OptionCard = ({
  logo,
  alt,
  custo,
  desciption,
  action,
  selected,
  loading,
  unavailable,
}: CardProp) => {
  return (
    <div
      role="radio"
      aria-checked={selected}
      onClick={!loading && !unavailable ? action : undefined}
      className={cn(
        `
        relative rounded-xl border bg-brand-card p-6 w-56 h-56
        transition-all duration-300 ease-in-out m-auto flex flex-col justify-center
        `,
        loading || unavailable
          ? "opacity-50 cursor-default"
          : "cursor-pointer",
        !loading && !unavailable && selected
          ? "border-brand-text shadow-xl shadow-brand-shadow opacity-100 translate-y-[-2px]"
          : !loading && !unavailable
          ? "border-brand-text opacity-50 hover:opacity-100 hover:shadow-md"
          : "border-brand-text",
      )}
    >
      {/* Ícone de seleção */}
      {!loading && !unavailable && (
        <div className="absolute right-4 top-4">
          {selected ? (
            <SquareCheckBig className="h-6 w-6 text-brand-text" />
          ) : (
            <Square className="h-6 w-6 text-brand-text" />
          )}
        </div>
      )}

      {/* Logo */}
      <div className="mb-4 flex items-center justify-center h-20 px-4">
        <img
          src={logo}
          alt={alt}
          className="object-contain max-h-full max-w-full"
        />
      </div>

      {/* Preço */}
      {loading ? (
        <div className="mb-2 flex flex-col gap-1.5">
          <div className="h-7 w-32 rounded bg-brand-sidebar/20 animate-pulse" />
          <div className="h-4 w-20 rounded bg-brand-sidebar/10 animate-pulse" />
        </div>
      ) : unavailable ? (
        <p className="mb-2 text-sm text-brand-text/50 italic">Indisponível</p>
      ) : (
        <p className="mb-2 text-brand-sidebar">
          <span className="text-2xl font-bold">USD {custo.toFixed(2)}</span>
          <span className="text-base font-normal"> (frete + taxas)</span>
        </p>
      )}

      {/* Descrição */}
      <p className="text-sm text-brand-text leading-relaxed">{desciption}</p>
    </div>
  );
};
